import React, { useState, useCallback } from 'react';
import { X, AlertTriangle, Check, Gift, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiBase';

// 3-step cancellation flow:
//   Step 1: Win-back offers (discount, downgrade, pause)
//   Step 2: Reason picker (survey — why you leaving?)
//   Step 3: Confirmation ("you'll keep access until X, data stays safe")
// FTC-compliant because "just cancel" is always one click from step 1.

const REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_using_enough', label: "I'm not using it enough" },
  { id: 'missing_feature', label: 'Missing a feature I need' },
  { id: 'found_job', label: 'I found a new job 🎉' },
  { id: 'bug_issue', label: 'Ran into too many bugs or issues' },
  { id: 'other', label: 'Something else' },
];

const CancellationModal = ({ open, subscription, onClose, onCancelled }) => {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resetAndClose = useCallback(() => {
    setStep(1);
    setReason('');
    setReasonNote('');
    setError('');
    onClose();
  }, [onClose]);

  const submitCancellation = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      const reasonFull = reason + (reasonNote ? `: ${reasonNote.slice(0, 200)}` : '');
      const res = await axios.post(
        `${API}/payments/cancel`,
        { reason: reasonFull || 'not_provided' },
        { withCredentials: true }
      );
      if (window.gtag) {
        window.gtag('event', 'subscription_cancelled', { reason: reason || 'not_provided' });
      }
      onCancelled?.(res.data);
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not cancel. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [reason, reasonNote, onCancelled]);

  if (!open) return null;

  const accessUntil = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : 'the end of your billing period';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="cancel-modal">
      <div className="bg-white max-w-lg w-full p-8 shadow-2xl relative">
        <button
          onClick={resetAndClose}
          className="absolute top-4 right-4 text-[#9ca3af] hover:text-[#0f1115]"
          aria-label="Close"
          data-testid="cancel-modal-close"
        >
          <X size={20} />
        </button>

        {step === 1 && (
          <div data-testid="cancel-step-1">
            <h2 className="font-satoshi text-2xl font-bold text-[#0f1115] mb-2">
              Before you go&hellip;
            </h2>
            <p className="text-[#4b5563] mb-6 text-sm">
              We get it — things change. Before cancelling, would any of these help?
            </p>

            <div className="space-y-3 mb-6">
              <div className="border border-[#e5e7eb] p-4 flex items-start gap-3">
                <Gift size={20} className="text-[#002fa7] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-[#0f1115] text-sm">30% off your next 3 months</p>
                  <p className="text-[#6b7280] text-xs mt-1">We'll email you a one-click re-activation link after cancellation if you change your mind within 7 days.</p>
                </div>
              </div>
              <div className="border border-[#e5e7eb] p-4 flex items-start gap-3">
                <Check size={20} className="text-[#10b981] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-[#0f1115] text-sm">Keep access until {accessUntil}</p>
                  <p className="text-[#6b7280] text-xs mt-1">No immediate downgrade. All your resumes, applications, and cover letters stay safe forever.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={resetAndClose}
                className="flex-1 py-3 border border-[#002fa7] text-[#002fa7] font-semibold hover:bg-[#002fa7] hover:text-white transition-colors"
                data-testid="cancel-step-1-keep"
              >
                Keep my subscription
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 text-[#6b7280] font-semibold hover:text-[#e11d48] transition-colors"
                data-testid="cancel-step-1-proceed"
              >
                Continue cancelling
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="cancel-step-2">
            <h2 className="font-satoshi text-2xl font-bold text-[#0f1115] mb-2">
              One quick question
            </h2>
            <p className="text-[#4b5563] mb-6 text-sm">
              What made you decide to cancel? (This helps us get better.)
            </p>
            <div className="space-y-2 mb-4">
              {REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${reason === r.id ? 'border-[#002fa7] bg-[#002fa7]/5' : 'border-[#e5e7eb] hover:border-[#9ca3af]'}`}
                  data-testid={`cancel-reason-${r.id}`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                    className="accent-[#002fa7]"
                  />
                  <span className="text-sm text-[#0f1115]">{r.label}</span>
                </label>
              ))}
            </div>
            <textarea
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value.slice(0, 200))}
              placeholder="Anything else we should know? (Optional)"
              className="w-full p-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] focus:outline-none mb-4 min-h-[80px]"
              maxLength={200}
              data-testid="cancel-reason-note"
            />
            {error && <p className="text-[#e11d48] text-sm mb-3" data-testid="cancel-error">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 border border-[#e5e7eb] text-[#4b5563] font-semibold hover:bg-[#f4f5f7] transition-colors"
                data-testid="cancel-step-2-back"
              >
                Back
              </button>
              <button
                onClick={submitCancellation}
                disabled={submitting || !reason}
                className="flex-1 py-3 bg-[#e11d48] text-white font-semibold hover:bg-[#be123c] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cancel-step-2-submit"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                Confirm cancellation
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center" data-testid="cancel-step-3">
            <div className="w-14 h-14 mx-auto bg-[#ecfdf5] border-2 border-[#10b981] flex items-center justify-center mb-4">
              <Check size={28} className="text-[#10b981]" />
            </div>
            <h2 className="font-satoshi text-2xl font-bold text-[#0f1115] mb-2">
              Cancellation scheduled
            </h2>
            <p className="text-[#4b5563] mb-2 text-sm">
              You'll keep full access until <strong className="text-[#0f1115]">{accessUntil}</strong>.
            </p>
            <p className="text-[#6b7280] text-xs mb-6">
              All your data stays safe forever. You can resume any time before then with one click.
            </p>
            <button
              onClick={resetAndClose}
              className="w-full py-3 bg-[#002fa7] text-white font-semibold hover:bg-[#001f70] transition-colors"
              data-testid="cancel-step-3-close"
            >
              Got it
            </button>
          </div>
        )}
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs flex items-center gap-2" role="note">
        <AlertTriangle size={12} /> No charge after cancellation — we never auto-renew ended subscriptions.
      </div>
    </div>
  );
};

export default CancellationModal;
